import React from 'react';
import styled from 'styled-components';
import { useDesignContext } from '../hooks/useDesignContext';

const Container = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
  z-index: 10000;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 20px;
  padding: 40px;
  max-width: 900px;
  width: 100%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1a202c;
  text-align: center;
  margin: 0;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Option = styled.div<{ $primary?: boolean }>`
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #f8fafc;

  &:hover {
    border-color: #5D722C;
    transform: translateY(-4px);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    background: #ffffff;
  }

  svg {
    width: 64px;
    height: 64px;
    color: #5D722C;
  }
`;

const OptionTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
`;

const OptionDesc = styled.p`
  font-size: 15px;
  color: #64748b;
  text-align: center;
  margin: 0;
  line-height: 1.5;
`;

const LandingPage: React.FC<{ onSelect: (mode: 'LSP/QD' | 'ESQ') => void }> = ({ onSelect }) => {
  const { setDesignMode } = useDesignContext();

  const handleSelect = (mode: 'LSP/QD' | 'ESQ') => {
    setDesignMode(mode);
    try {
      sessionStorage.setItem('design_tool.mode_selected', 'true');
      sessionStorage.setItem('design_tool.page_mode', mode);
      localStorage.setItem('design_tool.mode', mode);
    } catch { }
    onSelect(mode);
  };

  return (
    <Container>
      <Card>
        <Title>Welcome to the Design Tool</Title>
        <OptionsGrid>
          <Option onClick={() => handleSelect('LSP/QD')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="3" x2="9" y2="21" />
            </svg>
            <OptionTitle>LSP / QD</OptionTitle>
            <OptionDesc>Full flexible designer with advanced positioning and customization tools.</OptionDesc>
          </Option>
          <Option onClick={() => handleSelect('ESQ')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
            </svg>
            <OptionTitle>ESQ</OptionTitle>
            <OptionDesc>Template-based design with fixed positions for quick and consistent memorials.</OptionDesc>
          </Option>
        </OptionsGrid>
      </Card>
    </Container>
  );
};

export default LandingPage;
